import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Loader2, Search, PauseCircle, PlayCircle, Trash2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Badge({ children, tone }) {
  const tones = { green: "bg-emerald-50 text-emerald-700 border-emerald-200", amber: "bg-amber-50 text-amber-700 border-amber-200", rose: "bg-rose-50 text-rose-700 border-rose-200", slate: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
}

export function AdminPages({ adminToken, tick }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [mode, setMode] = useState("period_end");
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/pages`, { params: { q }, headers: { Authorization: `Bearer ${adminToken}` } }).then(r => setItems(r.data.items)).catch(() => toast.error("Seiten konnten nicht geladen werden"));
  }, [adminToken, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, tick]);

  const confirm = async () => {
    setBusy(true);
    try {
      if (dlg.type === "delete") {
        const r = await axios.post(`${API}/admin/pages/${dlg.item.id}/delete`, { mode }, { headers });
        toast.success(r.data.mode === "immediate" ? "Seite wurde abgebaut und gelöscht" : "Kündigung zum Laufzeitende ausgelöst – Abbau erfolgt automatisch");
      } else {
        await axios.post(`${API}/admin/pages/${dlg.item.id}/deactivate`, { deactivate: dlg.type === "deactivate" }, { headers });
        toast.success(dlg.type === "deactivate" ? "Seite deaktiviert" : "Seite wieder aktiviert");
      }
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-pages-management">
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Suche nach Firma, E-Mail oder Domain" value={q} onChange={e => setQ(e.target.value)} data-testid="admin-pages-search" />
      </div>
      {!items ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500 border-b border-slate-200"><tr><th className="px-4 py-3">Seite</th><th className="px-3 py-3">Paket</th><th className="px-3 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400" data-testid="admin-pages-empty">Keine Seiten gefunden</td></tr>}
              {items.map(it => (
                <tr key={it.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70" data-testid={`admin-page-row-${it.id}`}>
                  <td className="px-4 py-3"><p className="font-semibold text-slate-900">{it.company_name || "–"}</p><p className="text-xs text-slate-500">{it.domain || "keine Domain"} · {it.email || "keine E-Mail"}</p></td>
                  <td className="px-3 py-3 text-slate-600">{it.package || "–"}</td>
                  <td className="px-3 py-3 space-x-1">
                    {it.is_deactivated ? <Badge tone="rose">Deaktiviert</Badge> : it.cancel_at_period_end ? <Badge tone="amber">Gekündigt</Badge> : it.sub_status === "active" ? <Badge tone="green">Aktiv</Badge> : <Badge tone="slate">Ohne Abo</Badge>}
                  </td>
                  <td className="px-4 py-3"><div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setDlg({ type: it.is_deactivated ? "reactivate" : "deactivate", item: it })} data-testid={`admin-page-btn-deactivate-${it.id}`}>
                      {it.is_deactivated ? <PlayCircle className="w-3.5 h-3.5 mr-1" /> : <PauseCircle className="w-3.5 h-3.5 mr-1" />}{it.is_deactivated ? "Aktivieren" : "Deaktivieren"}
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setMode(it.has_stripe ? "period_end" : "immediate"); setDlg({ type: "delete", item: it }); }} data-testid={`admin-page-btn-delete-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Löschen
                    </Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-w-md rounded-2xl" data-testid={dlg?.type === "delete" ? "admin-dialog-delete-confirm" : "admin-dialog-deactivate-confirm"}>
          <DialogHeader>
            <DialogTitle>{dlg?.type === "delete" ? "Seite löschen / Vertrag kündigen" : dlg?.type === "deactivate" ? "Seite deaktivieren" : "Seite aktivieren"}</DialogTitle>
            <DialogDescription>{dlg?.item.company_name} · {dlg?.item.domain || "ohne Domain"}</DialogDescription>
          </DialogHeader>
          {dlg?.type === "deactivate" && <p className="text-sm text-slate-600">Besucher sehen sofort einen Pausenhinweis statt der Seite. Abo und Domain bleiben bestehen; die Aktion ist jederzeit umkehrbar.</p>}
          {dlg?.type === "reactivate" && <p className="text-sm text-slate-600">Die Seite ist danach wieder öffentlich erreichbar.</p>}
          {dlg?.type === "delete" && (
            <div className="space-y-3 text-sm">
              <label className={`flex gap-2 border rounded-xl p-3 cursor-pointer ${mode === "period_end" ? "border-indigo-300 bg-indigo-50" : "border-slate-200"} ${dlg.item.has_stripe ? "" : "opacity-50"}`}>
                <input type="radio" disabled={!dlg.item.has_stripe} checked={mode === "period_end"} onChange={() => setMode("period_end")} data-testid="admin-delete-mode-period-end" />
                <span><strong>Zum Laufzeitende</strong> (wie „Vertrag kündigen“): Stripe-Abo wird gekündigt, der Kunde bekommt die Kündigungs-Mail, Domain/Hosting/Seite werden nach Ablauf automatisch abgebaut.</span>
              </label>
              <label className={`flex gap-2 border rounded-xl p-3 cursor-pointer ${mode === "immediate" ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}>
                <input type="radio" checked={mode === "immediate"} onChange={() => setMode("immediate")} data-testid="admin-delete-mode-immediate" />
                <span><strong>Sofort löschen</strong>: Abo wird sofort beendet, Cloudflare-Zone, INWX-Domain, Seite und Leads werden unwiderruflich entfernt.</span>
              </label>
              {mode === "immediate" && <p className="flex gap-2 text-rose-700 text-xs"><AlertTriangle className="w-4 h-4 flex-shrink-0" />Nicht umkehrbar.</p>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setDlg(null)} data-testid="admin-dialog-cancel">Abbrechen</Button>
            <Button onClick={confirm} disabled={busy} className={dlg?.type === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"} data-testid="admin-dialog-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : dlg?.type === "delete" ? "Bestätigen" : "Ja, fortfahren"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
